import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Plus } from "lucide-react";
import type { ProjectRecord } from "../../backend/api/generated/openapi";
import {
  PROJECT_LIST_LIMIT,
  useProjectList,
} from "../../backend/queries/projects";
import {
  ProjectStatusBadge,
  ProjectTypeBadge,
} from "../../components/domain-badges";
import {
  DataPagination,
  DataView,
  EmptyState,
  FilterBar,
  FilterField,
  Page,
  PageHeader,
} from "../../components/patterns";
import type { DataColumn } from "../../components/patterns";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useProjectMetadata } from "../../lib/useProjectMetadata";
import ProjectForm from "./projectform";
import Modal from "./projectmodal";

const PAGE_SIZE = 10;
const ALL = "All";

const personName = (
  person?: {
    firstname?: string | null;
    lastname?: string | null;
  } | null,
) => [person?.firstname, person?.lastname].filter(Boolean).join(" ").trim();

const columns: DataColumn<ProjectRecord>[] = [
  {
    id: "id",
    header: "ID",
    className: "w-20",
    cell: (project) => <span className="font-medium">#{project.id}</span>,
  },
  {
    id: "client",
    header: "Client",
    primary: true,
    cell: (project) => personName(project.customer) || "No client",
  },
  {
    id: "type",
    header: "Type",
    cell: (project) => <ProjectTypeBadge type={project.project_type} />,
  },
  {
    id: "from",
    header: "From",
    cell: (project) => project.relocating_from || "Not set",
  },
  {
    id: "to",
    header: "To",
    cell: (project) => project.relocating_to || "Not set",
  },
  {
    id: "status",
    header: "Status",
    cell: (project) => <ProjectStatusBadge status={project.status} />,
  },
  {
    id: "agent",
    header: "Agent",
    cell: (project) => personName(project.agent) || "Unassigned",
  },
];

const matchesSearch = (project: ProjectRecord, term: string) => {
  if (!term) return true;
  return [
    personName(project.customer),
    personName(project.agent),
    project.status,
    project.project_type,
    project.relocating_from,
    project.relocating_to,
  ].some((value) => value && value.toLowerCase().includes(term));
};

const ProjectsList = () => {
  const projects = useProjectList();
  const { metadata, error: metadataError } = useProjectMetadata();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);

  const all = useMemo(() => projects.data || [], [projects.data]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter(
      (project) =>
        matchesSearch(project, term) &&
        (typeFilter === ALL || project.project_type === typeFilter) &&
        (statusFilter === ALL || project.status === statusFilter),
    );
  }, [all, search, typeFilter, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const filtersApplied =
    search !== "" || typeFilter !== ALL || statusFilter !== ALL;
  // The endpoint returns at most PROJECT_LIST_LIMIT rows, newest first, so a
  // full page means older projects may be missing. Say so rather than imply
  // the list is complete.
  const capped = all.length >= PROJECT_LIST_LIMIT;

  const clearFilters = () => {
    setSearch("");
    setTypeFilter(ALL);
    setStatusFilter(ALL);
    setPage(1);
  };

  return (
    <Page width="full">
      <PageHeader title="Projects" />

      {metadataError && (
        <Alert variant="destructive">
          <AlertDescription>{metadataError}</AlertDescription>
        </Alert>
      )}

      <FilterBar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchLabel="Search projects"
        searchPlaceholder="Search by client, agent, place or status…"
        summary={
          projects.isPending
            ? undefined
            : `${filtered.length} of ${all.length} ${
                all.length === 1 ? "project" : "projects"
              }`
        }
        onClear={filtersApplied ? clearFilters : undefined}
        actions={
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4" />
            New project
          </Button>
        }
      >
        <FilterField label="Project type">
          {(controlId) => (
            <Select
              value={typeFilter}
              onValueChange={(value) => {
                setTypeFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger id={controlId} className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {metadata.project_types.map((type) => (
                  <SelectItem key={type.code} value={type.code}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FilterField>
        <FilterField label="Status">
          {(controlId) => (
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger id={controlId} className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {metadata.statuses.map((status) => (
                  <SelectItem key={status.code} value={status.code}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FilterField>
      </FilterBar>

      {capped && (
        <p className="text-sm text-muted-foreground">
          Showing the {PROJECT_LIST_LIMIT} most recent projects. Older projects
          are not listed here yet.
        </p>
      )}

      <DataView
        caption="Projects"
        columns={columns}
        rows={visible}
        getRowKey={(project) => project.id}
        getRowHref={(project) => `/projects/${project.id}`}
        rowActions={(project) => (
          <Button asChild variant="outline" size="icon">
            <Link
              to={`/projects/${project.id}?chat=1`}
              aria-label={`Open the conversation for project ${project.id}`}
              title="Open conversation"
            >
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        )}
        status={
          projects.isPending ? "loading" : projects.error ? "error" : "ready"
        }
        errorTitle="Projects could not be loaded"
        onRetry={() => void projects.refetch()}
        empty={
          filtersApplied ? (
            <EmptyState
              title="No projects match these filters"
              description="Try a different search, or clear the filters."
            />
          ) : (
            <EmptyState
              title="No projects yet"
              description="Projects you can access appear here."
            />
          )
        }
      />

      {pageCount > 1 && (
        <DataPagination
          page={currentPage}
          pageCount={pageCount}
          onPageChange={setPage}
        />
      )}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)}>
        <ProjectForm
          handleCloseForm={() => setShowForm(false)}
          onSuccess={async () => {
            await projects.refetch();
            setShowForm(false);
          }}
        />
      </Modal>
    </Page>
  );
};

export default ProjectsList;
